import {HttpClient, HttpEventType, HttpParams, HttpRequest, HttpResponse,} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Client} from "@app/admin/client.service";
import {FilterData} from '@app/assetfilter/filter.service';
import {InvalidGuid} from "@app/review/models";

import {BehaviorSubject, forkJoin, from, Observable, ReplaySubject, Subject} from 'rxjs';
import {distinctUntilChanged, filter, map, share} from 'rxjs/operators';
import {deserialize} from '../serializable';
import {Rect} from '../shared/euclid';
import {StateService} from '../state/state.service';

import {Asset, deserializeAsset, deserializeAssetArray} from './models';

export class PagedResponse {
    next: string;
    previous: string;
    results: any[];
    count: number;
}

@Injectable({
    providedIn: 'root',
})
export class AssetService {
    public filteredAssets: ReplaySubject<Asset[]>;
    public assetUpdated: Subject<Asset>;
    public loading: BehaviorSubject<boolean>;
    public insertedAssets: Subject<Asset>;

    private cache: Map<string, Asset>;
    private nextUrl: string;
    private streamId: string;
    private filterData: FilterData;
    private filteredStreamId: string;

    constructor(
        private stateService: StateService,
        private http: HttpClient,
    ) {
        this.filteredAssets = new ReplaySubject<Asset[]>(1);
        this.assetUpdated = new Subject<Asset>();
        this.loading = new BehaviorSubject<boolean>(false);
        this.insertedAssets = new Subject<Asset>();

        this.cache = new Map<string, Asset>();
        this.nextUrl = null;
        this.filterData = null;

        // FIXME: This will force the observable to have at least one emission.  This is probably not the right way
        // This also gives a false positive to the asset-list loading/empty template
        this.filteredAssets.next([]);

        // Handle all state changes
        this.stateService.state
            .pipe(
                filter((state) => {
                    return Boolean(state.streamId);
                }),
                distinctUntilChanged((before, after) => {
                    const a = before.filterData ? before.filterData.serialize() : null;
                    const b = after.filterData ? after.filterData.serialize() : null;
                    return a === b;
                }),
            )
            .subscribe((state) => {
                this.filterData = state.filterData;
                this.streamId = state.streamId;
                this.filter(true);
            });

        this.stateService.state
            .pipe(
                filter((state) => {
                    return state.viewingAsset === null && Boolean(state.streamId);
                }),
                map((state) => state.streamId),
                distinctUntilChanged(),
            )
            .subscribe((streamId) => {
                this.streamId = streamId;
                this.filter();
            });
    }

    filter(force = false) {
        if (this.filteredStreamId === this.streamId && !force) {
            return;
        }

        if (!this.streamId) {
            return;
        }

        this.loading.next(true);

        this.filteredStreamId = this.streamId;

        let url = `/api/asset/filter/?stream=${this.streamId}`;
        if (this.filterData != null) {
            url = `${url}&query=${this.filterData.serialize()}`;
        }

        this.http
            .get<PagedResponse>(url)
            .pipe(
                map((response) => {
                    this.nextUrl = null;
                    if (response.next !== null) {
                        const url = new URL(response.next);
                        this.nextUrl = response.next.replace(url.origin, '');
                    }
                    return deserializeAssetArray(response.results);
                }),
            )
            .subscribe(
                (assets) => {
                    this.cache.clear();
                    assets.forEach((asset) => this.cache.set(asset.id, asset));
                    this.filteredAssets.next(Array.from(this.cache.values()));
                    this.loading.next(false);
                },
                (error) => {
                    this.cache.clear();
                    this.filteredAssets.next(Array.from(this.cache.values()));
                    this.loading.next(false);
                },
            );
    }

    resolve(ids: string[]): Observable<Asset[]> {
        const promise = new Promise<Asset[]>((resolve, reject) => {
            let assets = [];
            const missing = [];
            ids.forEach((id) => {
                if (this.cache.has(id)) {
                    assets.push(this.cache.get(id));
                } else {
                    missing.push(id);
                }
            });

            if (assets.length !== ids.length) {
                // missing assets, query the server
                const missingGuids = missing.map(i => i.substring(0, InvalidGuid.length));
                const url = `/api/asset/?guids=${missingGuids.join(',')}`;
                this.http.get<PagedResponse>(url).subscribe((resolvedAssets) => {
                    assets = assets.concat(resolvedAssets.results);
                    for (const asset of assets) {
                        this.updateCacheAsset(asset);
                    }
                    resolve(assets);
                });
            } else {
                // Check for expired or still-processing assets. A cached Asset that isn't ready yet (e.g. video
                // still transcoding) must never be treated as a valid cache hit, or it will render as silently
                // broken forever instead of falling through to a refetch once it's actually done.
                if (assets.some((asset) => asset.isExpired() || asset.is_ready === false)) {
                    this.refreshAssets(assets).subscribe((assets) => resolve(assets));
                } else {
                    // We got everything from the cache
                    resolve(assets);
                }
            }
        });

        return from(promise);
    }

    setFilteredAssets(assets: Asset[]) {
        this.cache.clear();
        assets.forEach((asset) => this.cache.set(asset.id, asset));
        this.filteredAssets.next(Array.from(this.cache.values()));
        this.loading.next(false);
    }

    next() {
        if (this.nextUrl !== null) {
            const observable = this.http.get<PagedResponse>(this.nextUrl).pipe(
                map((response) => {
                    this.nextUrl = null;
                    if (response.next !== null) {
                        const url = new URL(response.next);
                        this.nextUrl = response.next.replace(url.origin, '');
                    }

                    return deserializeAssetArray(response.results);
                }),
                share(),
            );
            observable.subscribe((assets) => {
                assets.forEach((asset) => this.cache.set(asset.id, asset));
                this.filteredAssets.next(Array.from(this.cache.values()));
            });

            return observable;
        }
    }

    updateAsset(id: string, data: any) {
        const url = `/api/asset/${id}/`;
        const options = data;
        options['withCredentials'] = true;

        this.http
            .patch<Asset>(url, options)
            .pipe(map((asset) => deserializeAsset(asset)))
            .subscribe((asset) => {
                this.assetUpdated.next(asset);
            });
    }

    assignTags(ids: string[], add: string[], remove: string[]): Observable<Asset[]> {
        const url = '/api/asset/assignTags/';
        const options = {
            ids: ids,
            add: add,
            remove: remove,
            withCredentials: true,
        };

        const observable = this.http.put<Asset[]>(url, options).pipe(
            map((assets) => deserializeAssetArray(assets)),
            share(),
        );

        observable.subscribe((assets) => {
            // Update cache here
            assets.forEach((asset) => {
                this.updateCacheAsset(asset);
                this.assetUpdated.next(asset);
            });
        });

        return observable;
    }

    updateAssetThumbnail(file: File, asset: Asset): Observable<number> {
        const formData = new FormData();
        formData.append('file', file, file.name);

        const req = new HttpRequest('PATCH', `/api/asset/${asset.id}/`, formData, {
            reportProgress: true,
        });

        const progress = new Subject<number>();
        this.http.request(req).subscribe((event) => {
            if (event.type === HttpEventType.UploadProgress) {
                const percentDone = Math.round((100 * event.loaded) / event.total);
                progress.next(percentDone);
            } else if (event instanceof HttpResponse) {
                // We got a response, request is complete
                progress.complete();
                this.reserializeAsset(event.body as Asset);
            }
        });

        return progress.asObservable();
    }

    reserializeAsset(asset: Asset) {
        const cachedAsset = this.updateCacheAsset(deserializeAsset(asset));
        this.assetUpdated.next(cachedAsset);
    }

    resetThumbnail(asset: Asset) {
        const url = `/api/asset/${asset.id}/`;
        const options = {
            'reset-thumbnail': true,
            withCredentials: true,
        };

        this.http
            .patch(url, options)
            .pipe(map((asset) => deserialize(Asset, asset)))
            .subscribe((asset) => {
                this.reserializeAsset(asset);
            });
    }

    cropImage(asset: Asset, cropData: Rect) {
        const url = `/api/asset/${asset.id}/`;
        const options = {
            crop: [cropData.x, cropData.y, cropData.right, cropData.bottom],
            withCredentials: true,
        };

        this.http
            .patch(url, options)
            .pipe(map((asset) => deserialize(Asset, asset)))
            .subscribe((asset) => {
                this.reserializeAsset(asset);
            });
    }

    removeAssets(assetIds: string[]) {
        assetIds.forEach((assetId) => {
            if (this.cache.has(assetId)) {
                this.cache.delete(assetId);
            }
        });

        this.filteredAssets.next(Array.from(this.cache.values()));
    }

    deleteAssets(assetIds: string[]) {
        const assetIdsCopy = assetIds.slice(0);
        const observables = assetIdsCopy.map((assetId) => {
            return this.http.delete(`/api/asset/${assetId}/`);
        });

        forkJoin(observables).subscribe(() => {
            this.removeAssets(assetIdsCopy);
        });
    }

    viewAsset(asset: Asset) {
        const url = `/api/asset/${asset.id}/view/`;
        const options = {
            withCredentials: true,
        };
        this.http.post(url, options).subscribe((result) => {
            if (Object.keys(result).length > 0) {
                asset.view_count += 1;
                this.reserializeAsset(asset);
            }
        });
    }

    likeAsset(asset: Asset) {
        const url = `/api/asset/${asset.id}/like/`;
        const options = {
            withCredentials: true,
        };
        this.http.post(url, options).subscribe((result) => {
            if (Object.keys(result).length > 0) {
                asset.like_count += 1;
                this.reserializeAsset(asset);
            }
        });
    }

    listAssets(pageIndex: number = 0, ordering: string = null): Observable<PagedResponse> {
        let filterQuery = new HttpParams();
        filterQuery = filterQuery.set('page', String(pageIndex + 1));
        if (ordering) {
            filterQuery = filterQuery.set('ordering', ordering);
        }
        return this.http.get<PagedResponse>(`/api/asset/?${filterQuery.toString()}`);
    }

    history(assetId: string) {
        return this.http
            .get<Asset[]>(`/api/asset/${assetId}/history/`)
            .pipe(filter((revisions) => revisions.length > 0));
    }

    streams(assetId: string) {
        return this.http.get(`/api/asset/${assetId}/streams/`);
    }

    appendAsset(asset: Asset) {
        this.insertedAssets.next(asset);
    }

    clear() {
        this.cache.clear();
        this.filteredAssets.next(Array.from(this.cache.values()));
    }

    public clientForAsset(assetId: string): Observable<Client> {
        return this.http.get<Client>(`/api/asset/${assetId}/client/`);
    }

    public updateCacheAsset(asset: Asset): Asset {
        let cachedAsset = this.cache.get(asset.id);
        asset = deserializeAsset(asset);
        if (cachedAsset === null || cachedAsset === undefined) {
            // Cache miss, just add it to the cache
            this.cache.set(asset.id, asset);
            cachedAsset = asset;
        } else {
            // update the cached reference
            Object.assign(cachedAsset, asset);
        }

        return cachedAsset;
    }

    private refreshAssets(assets: Asset[]): Observable<Asset[]> {
        const url = `/api/asset/refresh/`;
        const options = {
            ids: assets.map((asset) => asset.id),
            withCredentials: true,
        };
        return this.http.post<Asset[]>(url, options).pipe(
            map((results) => {
                const assets = deserializeAssetArray(results);
                for (const asset of assets) {
                    this.updateCacheAsset(asset);
                }
                return assets;
            }),
        );
    }
}
